import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { api, USE_MOCK } from "../api/api.js";

// Nom stable pour chaque fonction de l'objet `api`, utilisé comme préfixe de
// clé de cache. Lookup plutôt que fetcherFn.name : le nom inféré peut
// disparaître à la minification en build de production, ce qui ferait
// collisionner toutes les requêtes sur une même clé.
const NOM_PAR_FETCHER = new Map(
  Object.entries(api).map(([nom, fn]) => [fn, nom])
);

// Les flags USE_MOCK font partie de la clé de cache. Sinon un résultat mock
// mis en cache (flag à true) continuait d'être servi après le passage à
// false : le cache vit dans QueryClient (index.js), qui n'est PAS recréé
// quand on modifie api.js à chaud, et staleTime: Infinity ne le refetch
// jamais tant que les paramètres sont identiques.
const SOURCE_DONNEES = JSON.stringify(USE_MOCK);

/**
 * useApiData(fetcherFn, params, { skip })
 * - fetcherFn : fonction (params) => Promise<data>, ex: api.getEau
 * - params    : objet passé au fetcher (doit être sérialisable)
 * - skip      : si true, ne fetch pas (ex: tant que les filtres ne sont pas prêts)
 *
 * Mise en cache par (fetcher + params) via TanStack Query : deux pages qui
 * demandent la même chose avec les mêmes paramètres ne déclenchent qu'un seul
 * appel réseau, et revenir sur une page déjà consultée est instantané.
 * L'annulation des requêtes obsolètes (sélecteur manipulé vite) est gérée par
 * la lib. Même signature et même retour { data, loading, error } qu'avant :
 * aucune page à modifier.
 */
export function useApiData(fetcherFn, params, { skip = false } = {}) {
  const nom = NOM_PAR_FETCHER.get(fetcherFn) ?? "inconnu";

  const { data, isPending, isPlaceholderData, error } = useQuery({
    queryKey: [nom, SOURCE_DONNEES, params],
    queryFn: () => fetcherFn(params),
    enabled: !skip,
    // Comme l'ancien hook : pendant qu'on charge de nouveaux paramètres, on
    // garde l'affichage précédent (loading vaut true) au lieu de repasser
    // data à null.
    placeholderData: keepPreviousData,
  });

  return {
    data: data ?? null,
    // isPending reste vrai quand la requête est désactivée (skip) : sans le
    // `!skip`, les pages afficheraient "Chargement…" en boucle avant que les
    // paramètres soient prêts.
    loading: !skip && (isPending || isPlaceholderData),
    error: error
      ? error?.response?.data?.message || error.message || "Erreur inconnue"
      : null,
  };
}
